/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalRecordDecision } from "../funcs/mcpApprovalRecordDecision.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApprovalDecision } from "../models/components/approvaldecision.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RecordMcpApprovalDecisionRequest,
  RecordMcpApprovalDecisionSecurity,
} from "../models/operations/recordmcpapprovaldecision.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RecordMcpApprovalDecisionMutationVariables = {
  request: RecordMcpApprovalDecisionRequest;
  security?: RecordMcpApprovalDecisionSecurity | undefined;
  options?: RequestOptions;
};

export type RecordMcpApprovalDecisionMutationData = ApprovalDecision;

export type RecordMcpApprovalDecisionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * recordDecision mcpApproval
 *
 * @remarks
 * Approve or deny an MCP approval request, recording the rationale and who it applies to.
 */
export function useRecordMcpApprovalDecisionMutation(
  options?: MutationHookOptions<
    RecordMcpApprovalDecisionMutationData,
    RecordMcpApprovalDecisionMutationError,
    RecordMcpApprovalDecisionMutationVariables
  >,
): UseMutationResult<
  RecordMcpApprovalDecisionMutationData,
  RecordMcpApprovalDecisionMutationError,
  RecordMcpApprovalDecisionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRecordMcpApprovalDecisionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRecordMcpApprovalDecision(): MutationKey {
  return ["@gram/client", "mcpApproval", "recordDecision"];
}

export function buildRecordMcpApprovalDecisionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RecordMcpApprovalDecisionMutationVariables,
  ) => Promise<RecordMcpApprovalDecisionMutationData>;
} {
  return {
    mutationKey: mutationKeyRecordMcpApprovalDecision(),
    mutationFn: function recordMcpApprovalDecisionMutationFn({
      request,
      security,
      options,
    }): Promise<RecordMcpApprovalDecisionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalRecordDecision(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
